/**
 * tiposCampana — Tipos, constantes y helpers del módulo de notificaciones push.
 *
 * Modelo de datos según docs/ANALISIS_IMPLEMENTACION_NOTIFICACIONES_FCM.md §5.1
 * y catálogo de botones §8.1.8.
 */
import type { Timestamp } from 'firebase/firestore';

// ─── Estados ─────────────────────────────────────────────────

export type EstadoCampana =
  | 'borrador'
  | 'programada'
  | 'recurrente'
  | 'enviando'
  | 'enviada'
  | 'cancelada'
  | 'error';

export const ESTADO_LABELS: Record<EstadoCampana, string> = {
  borrador: 'Borrador',
  programada: 'Programada',
  recurrente: 'Recurrente',
  enviando: 'Enviando',
  enviada: 'Enviada',
  cancelada: 'Cancelada',
  error: 'Error',
};

export type BadgeVariantCampana = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export const ESTADO_BADGE: Record<EstadoCampana, BadgeVariantCampana> = {
  borrador: 'neutral',
  programada: 'info',
  recurrente: 'info',
  enviando: 'warning',
  enviada: 'success',
  cancelada: 'neutral',
  error: 'error',
};

// ─── Botones de acción (catálogo fijo §8.1.8) ────────────────

export type BotonId = 'mas_info' | 'abrir' | 'agendar' | 'llamar';
export type BotonTipo = 'url' | 'deeplink';

export interface BotonCampana {
  id: BotonId;
  tipo: BotonTipo;
  valor: string;
}

export interface BotonCatalogoItem {
  id: BotonId;
  label: string;
  tipo: BotonTipo;
  /** Valor por defecto al seleccionarlo. */
  valorDefault: string;
  /** Ayuda del campo de acción. */
  hint: string;
}

export const CATALOGO_BOTONES: BotonCatalogoItem[] = [
  {
    id: 'mas_info',
    label: 'Más información',
    tipo: 'url',
    valorDefault: '',
    hint: 'URL https que se abre en el navegador',
  },
  {
    id: 'abrir',
    label: 'Ver en la app',
    tipo: 'deeplink',
    valorDefault: 'medisyspdp://campanas/{id}',
    hint: 'Deep link dentro de la app',
  },
  {
    id: 'agendar',
    label: 'Agendar cita',
    tipo: 'deeplink',
    valorDefault: 'medisyspdp://citas',
    hint: 'Deep link al flujo de citas',
  },
  {
    id: 'llamar',
    label: 'Llamar',
    tipo: 'url',
    valorDefault: '',
    hint: 'Número de teléfono (ej. +507 500-0000)',
  },
];

export const MAX_BOTONES = 2;

// ─── Recurrencia ─────────────────────────────────────────────

export type RecurrenciaTipo = 'diaria' | 'semanal' | 'mensual';

export interface RecurrenciaCampana {
  tipo: RecurrenciaTipo;
  diaSemana?: number; // 0-6 (0 = domingo)
  diaMes?: number; // 1-28
  hora: string; // 'HH:mm'
  finalizaEl?: Timestamp | null;
  maxEnvios?: number | null;
  enviosRealizados?: number;
  proximaEjecucion?: Timestamp;
}

export const DIAS_SEMANA: { value: number; label: string }[] = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
];

// ─── Vigencia (TTL) ──────────────────────────────────────────

export type TtlHoras = 24 | 48 | 72 | 168 | 672;

export const TTL_OPCIONES: { value: TtlHoras; label: string }[] = [
  { value: 24, label: '24 horas' },
  { value: 48, label: '48 horas' },
  { value: 72, label: '72 horas (recomendado)' },
  { value: 168, label: '7 días' },
  { value: 672, label: '28 días' },
];

export const TTL_AYUDA =
  'Si el teléfono está apagado o sin conexión y la vigencia vence, la notificación se ' +
  'descarta; la campaña seguirá visible en la app hasta esta fecha.';

// ─── Documento Firestore ─────────────────────────────────────

export interface StatsCampana {
  dispositivosObjetivo: number;
  aceptadosFcm: number;
  erroresToken: number;
  erroresOtros: number;
  aperturas: number;
  vistasEnApp: number;
}

export interface Campana {
  id: string;
  nombre: string;
  titulo: string;
  descripcion: string;
  imagenUrl: string | null;
  imagenStoragePath?: string | null;
  linkMasInfo: string | null;
  botones: BotonCampana[] | null;
  estado: EstadoCampana;
  programadaPara: Timestamp | null;
  recurrencia: RecurrenciaCampana | null;
  ttlHoras: number;
  vigenciaHasta: Timestamp;
  prioridad: 'normal' | 'alta';
  stats?: StatsCampana;
  creadoPor: string;
  creadoPorNombre: string;
  creadoEn: Timestamp;
  actualizadoEn?: Timestamp;
  enviadaEn?: Timestamp | null;
  /** Archivada: fuera de las vistas de trabajo del portal (métricas intactas). */
  archivada?: boolean;
}

export interface EnvioCampana {
  id: string;
  ejecutadaEn: Timestamp;
  stats: {
    dispositivosObjetivo: number;
    aceptadosFcm: number;
    erroresToken: number;
    erroresOtros: number;
    aperturas?: number;
  };
}

// ─── Contratos de callables ──────────────────────────────────

export interface RecurrenciaPayload {
  tipo: RecurrenciaTipo;
  diaSemana?: number;
  diaMes?: number;
  hora: string;
  finalizaEl?: number | null;
  maxEnvios?: number | null;
}

export interface CampanaPayload {
  nombre: string;
  titulo: string;
  descripcion: string;
  imagenUrl?: string | null;
  imagenStoragePath?: string | null;
  linkMasInfo?: string | null;
  botones?: BotonCampana[] | null;
  programadaPara?: number | null;
  recurrencia?: RecurrenciaPayload | null;
  ttlHoras: TtlHoras;
  prioridad: 'normal' | 'alta';
}

export interface CrearCampanaResult {
  campanaId: string;
  estado: EstadoCampana;
}

export interface EnviarCampanaResult {
  success: boolean;
  /** true = el envío quedó encolado en segundo plano (progreso en el detalle). */
  encolada: boolean;
}

export interface ContarDispositivosResult {
  total: number;
}

export interface DispositivoPrueba {
  plataforma: string;
  actualizadoEn: number | null;
}

export interface UsuarioPrueba {
  uid: string;
  nombre: string;
  cedula: string;
  dispositivos: DispositivoPrueba[];
}

/** Candidato cuando varias cuentas de la app comparten la misma cédula. */
export interface CandidatoPrueba {
  uid: string;
  email: string;
  nombre: string;
  dispositivos: number;
  yaAgregado: boolean;
}

export interface GestionarDispositivosResult {
  testUsuarios?: UsuarioPrueba[];
  /** Si hay varias cuentas con la misma cédula, el gestor elige una. */
  requiereSeleccion?: boolean;
  candidatos?: CandidatoPrueba[];
}

export interface ContenidoPrueba {
  titulo: string;
  descripcion: string;
  imagenUrl?: string;
  linkMasInfo?: string;
  botones?: BotonCampana[];
  prioridad?: 'normal' | 'alta';
}

export interface ResultadoPruebaDispositivo {
  plataforma: string;
  ok: boolean;
  error: string | null;
}

export interface EnviarPruebaResult {
  resultados: ResultadoPruebaDispositivo[];
}

// ─── Estado local de la imagen en el composer ────────────────

/**
 * La imagen se mantiene local (blob) hasta guardar; 'existente' representa una
 * imagen ya subida a Storage (modo edición) y 'url' una URL externa.
 */
export type ImagenCampanaState =
  | { tipo: 'existente'; url: string; storagePath: string | null }
  | { tipo: 'blob'; blob: Blob; previewUrl: string }
  | { tipo: 'url'; url: string };

/** URL mostrable de cualquier variante de imagen. */
export function imagenPreviewUrl(img: ImagenCampanaState | null): string | null {
  if (!img) return null;
  return img.tipo === 'blob' ? img.previewUrl : img.url;
}

// ─── Emojis curados (picker simple, sin dependencia externa) ─

export const EMOJIS_CURADOS: string[] = [
  '😀', '😊', '👍', '🙌', '👏', '🎉', '❤️', '💚', '✅', '⚠️',
  'ℹ️', '📢', '📣', '📅', '⏰', '🔔', '🩺', '💉', '💊', '🏥',
  '🚑', '😷', '🤒', '🩹', '🧠', '🫀', '🦷', '👶', '🤰', '🍎',
  '💧', '🏃',
];

// ─── Helpers de fecha (hora de Panamá) ───────────────────────

const FMT_FECHA_HORA = new Intl.DateTimeFormat('es-PA', {
  timeZone: 'America/Panama',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const FMT_FECHA = new Intl.DateTimeFormat('es-PA', {
  timeZone: 'America/Panama',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export function fmtFechaHora(ts: Timestamp | Date | number | null | undefined): string {
  if (ts === null || ts === undefined) return '—';
  const date =
    typeof ts === 'number' ? new Date(ts) : ts instanceof Date ? ts : ts.toDate();
  return FMT_FECHA_HORA.format(date);
}

export function fmtFecha(ts: Timestamp | Date | number | null | undefined): string {
  if (ts === null || ts === undefined) return '—';
  const date =
    typeof ts === 'number' ? new Date(ts) : ts instanceof Date ? ts : ts.toDate();
  return FMT_FECHA.format(date);
}

/** "hace 3 min", "hace 2 h", "hace 5 días" */
export function fmtRelativo(ms: number | null | undefined): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'ahora';
  const seg = Math.floor(diff / 1000);
  if (seg < 60) return 'hace un momento';
  const min = Math.floor(seg / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return d === 1 ? 'hace 1 día' : `hace ${d} días`;
  const mes = Math.floor(d / 30);
  return mes === 1 ? 'hace 1 mes' : `hace ${mes} meses`;
}

export function pctSeguro(parte: number, total: number): string {
  if (!total) return '—';
  return `${Math.round((parte / total) * 100)}%`;
}

/** Slug simple para utm_campaign cuando aún no existe campanaId. */
export function slugify(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'campana';
}

/** Descripción legible de una recurrencia. */
export function describirRecurrencia(rec: RecurrenciaCampana | RecurrenciaPayload | null | undefined): string {
  if (!rec) return '—';
  const hora = rec.hora ?? '';
  if (rec.tipo === 'diaria') return `Diaria a las ${hora}`;
  if (rec.tipo === 'semanal') {
    const dia = DIAS_SEMANA.find((d) => d.value === rec.diaSemana)?.label ?? '';
    return `Semanal · ${dia} a las ${hora}`;
  }
  return `Mensual · día ${rec.diaMes ?? '—'} a las ${hora}`;
}

// ─── Validaciones de URLs ────────────────────────────────────

export function esUrlHttps(valor: string): boolean {
  try {
    const u = new URL(valor);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

const EXT_IMAGEN = /\.(jpe?g|png|webp)(\?.*)?$/i;

export function esUrlImagen(valor: string): boolean {
  return esUrlHttps(valor) && EXT_IMAGEN.test(valor);
}

export function esTelefono(valor: string): boolean {
  return /^\+?[0-9\s\-()]{7,20}$/.test(valor.trim());
}

// ─── Textos fijos de la especificación ───────────────────────

export const NOTA_IOS_IMAGEN =
  'En Android la imagen se muestra expandida; en iPhone solo al mantener presionada la ' +
  'notificación. En esta fase, iPhone muestra la notificación sin imagen (la imagen sí se ' +
  've en la pantalla Campañas de la app).';

export const NOTA_BIGQUERY =
  '"Entregado" real solo está disponible para Android vía BigQuery; iPhone no reporta ' +
  'entrega — use Aperturas como indicador.';

export const ADVERTENCIA_BOTONES = 'Los botones se ven al expandir la notificación.';

// ─── Límites de contenido ────────────────────────────────────

export const MAX_TITULO = 65;
export const WARN_TITULO = 50;
/** Máximo del campo descripción. La NOTIFICACIÓN la truncan los sistemas
 *  (Android ~240, iOS ~150 — ver TRUNCADO_PREVIEW), pero el texto COMPLETO
 *  siempre es visible en la app móvil al abrir la promoción, así que el
 *  campo permite más que lo que cabe en el push. */
export const MAX_DESCRIPCION = 500;
/** A partir de aquí el contador se pinta de advertencia: el texto ya no cabe
 *  completo en la notificación de Android (el corte más generoso). */
export const WARN_DESCRIPCION = 240;

/** Truncado por plataforma para el preview de la NOTIFICACIÓN. */
export const TRUNCADO_PREVIEW = {
  ios: { titulo: 50, descripcion: 150 },
  android: { titulo: 65, descripcion: 240 },
} as const;

/** Nota fija bajo el campo descripción (límites del push vs app). */
export const NOTA_DESCRIPCION_PUSH =
  'En la notificación el texto se corta: Android muestra ~240 caracteres y ' +
  'iOS ~150 (revise el preview a la derecha para ver cómo se verá). El texto ' +
  'completo siempre será visible en la app móvil al abrir la promoción.';
